import { CompletePolicy, Policy, PolicyBalance, PolicyUi, Risk } from '@/common/Policy';
import Auth from '@/services/Auth';
import BillPay from '@/services/BillPay';
import store from '@/store/index';
import { cleanString } from '@/utils/Utils';
import * as Sentry from '@sentry/browser';
import axios, { AxiosPromise, AxiosResponse } from 'axios';
import moment from 'moment';

// This function is declared outside of the Policy service below because overloads are not supported
// when using object initializers.
function isCompletePolicy(policy: PolicyUi): policy is PolicyUi<CompletePolicy>;
function isCompletePolicy(policy: Policy): policy is CompletePolicy {
	// Simply test for the presence of the first and last properties declared in
	// `CompletePolicy`.
	return 'balance' in policy && 'risks' in policy;
}

const Policy = {
	getBrokerPolicies(brokerageId: string): AxiosPromise {
		return axios({
			baseURL: process.env.VUE_APP_API_URI2,
			method: 'GET',
			params: {
				brokerageId,
			},
			url: '/brokerage/policies',
		});
	},

	getRiskData(policyNumber: string, clientId: string): AxiosPromise {
		return axios({
			baseURL: process.env.VUE_APP_API_URI,
			method: 'GET',
			params: {
				clientId,
				policy_number: policyNumber,
			},
			url: '/policy',
		});
	},

	getPolicyData(): AxiosPromise<Policy[]> {
		return axios({
			baseURL: process.env.VUE_APP_API_URI,
			method: 'GET',
			url: '/policy',
		});
	},

	/**
	 * Get and Set the Policy information in our vuex module. We also check to see if the user has BILL_PAY permissions, and if so, also fetch their Balance information for each policy.
	 */
	async setPolicyData(): Promise<PolicyUi[]> {
		try {
			const policyData: AxiosResponse<Policy[]> = await this.getPolicyData();
			const policyUis: PolicyUi[] = !Array.isArray(policyData.data)
				? []
				: policyData.data.map(
						(policy: Policy): PolicyUi => {
							// Find the main risk; it is used to populate the extra convenience fields
							// of `PolicyUi`. If the main risk is not present, reasonable defaults are
							// used instead.
							const mainRisk: Risk | undefined =
								policy.risks &&
								Object.values(policy.risks).find(
									(risk: Risk): boolean => risk?.main_risk === 'Y',
								);

							// Return a PolicyUi instance.
							const orgName: string | undefined = mainRisk?.insured?.org
								? cleanString(mainRisk.insured.org)
								: undefined;

							const insuredName: string | undefined = mainRisk?.insured?.name
								? cleanString(mainRisk.insured.name)
								: undefined;

							let riskName: string | undefined = orgName ?? insuredName;
							riskName = riskName ?? policy.policy_holder.full_name;

							const dropDownName: string = riskName
								? `${riskName} - ${policy.policy_number}`
								: policy.policy_number;

							const dropDownPolicyHolderName: string = policy?.policy_holder
								? `${policy.policy_holder.full_name} - ${policy.policy_number}`
								: policy.policy_number;

							const policyUi: PolicyUi = {
								...policy,
								dropDownName,
								dropDownPolicyHolderName,
								riskName: riskName ?? 'Unknown',
							};
							return policyUi;
						},
				  );
			store.dispatch('Policy/setPolicyData', policyUis);

			// Get the latest and greatest balance information for each policy. Note that these
			// updates occur in the background; i.e. they are not awaited upon as part of
			// this initial policy fetch.
			if (Auth.checkPermission('BILL_PAY')) {
				async function updateBalances(): Promise<void> {
					store.dispatch('Policy/setBalanceUiLoading', true);
					try {
						const updatedPolicyUis: PolicyUi[] = await Promise.all(
							policyUis.map(
								async (policyUi: PolicyUi): Promise<PolicyUi> => {
									try {
										const response: AxiosResponse<PolicyBalance> = await BillPay.getRawBalance(
											policyUi.policy_number,
										);
										const updatedPolicyUi: PolicyUi = {
											...policyUi,
											balance: response.data,
											balanceUi: BillPay.nonnegativeBalance(response.data),
										};
										return updatedPolicyUi;
									} catch (error) {
										console.error(error);
										Sentry.captureException(error);
									}
								},
							),
						);
						store.dispatch('Policy/setPolicyData', updatedPolicyUis);
					} catch (error) {
						console.error(error);
						Sentry.captureException(error);
					} finally {
						store.dispatch('Policy/setBalanceUiLoading', false);
					}
				}
				updateBalances();
			}

			return policyUis;
		} catch (error) {
			console.error(error);
			Sentry.captureException(error);
			throw error;
		}
	},

	getBalanceUiAccount(policy: PolicyUi): number | undefined {
		// The final `?? undefined` is used for converting nulls.
		return policy.balanceUi?.account ?? policy.balance?.account ?? undefined;
	},

	getBalanceUiDue(policy: PolicyUi): number | undefined {
		// The final `?? undefined` is used for converting nulls.
		return policy.balanceUi?.due ?? policy.balance?.due ?? undefined;
	},

	isCompletePolicy,

	/**
	 * @returns The given `policy` if it is active, or if it is cancelled but satisfies
	 * `canceledPolicyCriteria`; `undefined` otherwise.
	 */
	canceledPolicy(policy: PolicyUi, canceledPolicyCritera): PolicyUi | undefined {
		const status = policy?.policy_status;

		if (status !== 'CANCEL') {
			return policy;
		}

		if (canceledPolicyCritera.allowCanceled && canceledPolicyCritera.allow30Days) {
			const effectiveToDate = policy?.effective_to_date;
			if (!effectiveToDate) {
				return;
			}

			const momentEffectiveToDate = moment(effectiveToDate);
			const now = moment();
			const diff = now.diff(momentEffectiveToDate, 'days');
			if (30 >= diff) {
				return policy;
			} else {
				return;
			}
		}
	},
};

export default Policy;
